import React, { useMemo, useState } from 'react';
import { Button, Form, message, Modal } from 'antd';
import cx from 'classnames';
import dayjs from 'dayjs';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons';
import { CustomEhrModal, EhrConnectFlow } from 'components/EhrConnect';
import { deleteEhrConnection } from 'api/ehr';
import useEhrConnections from 'hooks/useEhrConnections';
import useEhrOptions from 'hooks/useEhrOptions';
import EhrBadge from '../EhrBadge';
import EhrBillingCheckList from '../EhrBillingCheckList';
import HiddenFormField from '../HiddenFormField';
import { ehrByName } from '../../constants';
import { fetchOnboardingVo, STEP_API, stepValuesToDto } from '../../billingReviewData';
import { formatTodayStartDate } from '../../utils';
import { ClaimScopeField, ClaimStartFields, SCOPE_OPTIONS, START_OPTIONS } from '../../steps/StepEhrScope';
import s from './s.module.less';

type Props = {
    onSaveSuccess?: () => void;
};

// Settings 的 EHR & billing scope tab：checkbox 列表列出所有已连接 EHR，勾选 = 纳入计费范围
// （billingEhrs），勾/取消即时保存（取消最后一个时弹确认）；Disconnect 为带确认的取消勾选，
// 均不断开真实连接。连接/重连复用 EhrConnectFlow。
export default function EhrScopeSettings({ onSaveSuccess }: Props) {
    const form = Form.useFormInstance();
    const billingEhrs: string[] = Form.useWatch(['ehr', 'billingEhrs'], form) || [];
    const claimScope = Form.useWatch(['ehr', 'claimScope'], form);
    const [connections, , refreshConnections] = useEhrConnections();
    const [ehrOptions] = useEhrOptions();
    // 正在走 connect / re-verify 流程的 EHR 名
    const [flowEhr, setFlowEhr] = useState<string | null>(null);
    // "My EHR isn't listed"（自建/未收录 EHR 登记）弹窗
    const [customOpen, setCustomOpen] = useState(false);
    const [scopeEditing, setScopeEditing] = useState(false);
    const [scopeSnap, setScopeSnap] = useState<string | undefined>(undefined);
    const [scopeSaving, setScopeSaving] = useState(false);
    const claimStartMode = Form.useWatch(['ehr', 'claimStartMode'], form);
    const claimStartDate = Form.useWatch(['ehr', 'claimStartDate'], form);
    // today 模式后端回的固定起始日（只读，hydrate / 保存后重拉写入）
    const claimTodayStartDate: string | undefined = Form.useWatch(['ehr', 'claimTodayStartDate'], form);
    const [startEditing, setStartEditing] = useState(false);
    const [startSnap, setStartSnap] = useState<{ mode?: string; date?: unknown }>({});
    const [startSaving, setStartSaving] = useState(false);

    const availableEhrs = useMemo(() => ehrOptions.filter((e) => !e.upcoming), [ehrOptions]);
    const upcomingEhrs = useMemo(() => ehrOptions.filter((e) => e.upcoming), [ehrOptions]);

    // 保存整个 ehr slice（claimStart 等未展示字段原样保留）
    const saveEhrSlice = async () => {
        const ehrValues = form.getFieldsValue(true)?.ehr || {};
        const dto = stepValuesToDto('claim-scope', { ehr: ehrValues });
        const res: any = await STEP_API['claim-scope'].save(dto);
        if (res?.error) throw new Error(res.error);
        onSaveSuccess?.();
    };

    // 真实连接状态：connected = 连接成功 / error = 有连接记录但已失效（可重连）/ none = 从未连接
    const connStatusOf = (name: string): 'connected' | 'error' | 'none' => {
        const c = connections.find((item) => item.ehrSystem === name);
        if (!c) return 'none';
        return c.connectionStatus === 'connected' ? 'connected' : 'error';
    };

    // 勾/取消即时保存；失败回滚勾选态（错误已由全局 toast 展示）
    const applyBillingEhrs = async (next: string[], okMsg?: string) => {
        const prev = billingEhrs;
        form.setFieldValue(['ehr', 'billingEhrs'], next);
        try {
            await saveEhrSlice();
            message.success(okMsg || 'Changes saved.');
        } catch (e) {
            form.setFieldValue(['ehr', 'billingEhrs'], prev);
        }
    };

    // 确认弹窗（对照原型）：无图标、大标题、右对齐大圆角按钮
    const confirmModalProps = {
        icon: null,
        className: s.confirmModal,
        okButtonProps: { danger: true, type: 'primary' as const },
    };

    // 取消最后一个不做前端拦截：后端会报错 → 全局 toast + 勾选态回滚
    const handleCheckChange = (next: string[]) => {
        applyBillingEhrs(next);
    };

    // 真实断开连接（删除 connection 记录，与 dashboard 的 EhrConnectModal 一致）；
    // 不动 billingEhrs——勾选态只是计费范围，断开后行从选项里消失，重连时按原勾选态回显
    const handleDisconnect = (name: string) => {
        const conn = connections.find((c) => c.ehrSystem === name);
        if (!conn) return;
        Modal.confirm({
            ...confirmModalProps,
            title: `Disconnect ${name}?`,
            content: `Klarity will stop billing claims from ${name}. Existing claims are not affected, and you can reconnect anytime.`,
            okText: 'Disconnect',
            onOk: async () => {
                const res: any = await deleteEhrConnection(conn.id);
                if (res && !res.error) {
                    message.success(`${name} disconnected`);
                    refreshConnections();
                }
                // 失败已由全局 toast 展示
            },
        });
    };

    // connect / 重连成功：加入计费范围并保存，刷新连接列表
    const handleFlowSuccess = async () => {
        const name = flowEhr!;
        setFlowEhr(null);
        refreshConnections();
        if (!billingEhrs.includes(name)) {
            form.setFieldValue(['ehr', 'billingEhrs'], [...billingEhrs, name]);
            try {
                await saveEhrSlice();
            } catch (e) {
                // 错误已由全局 toast 展示
            }
        }
    };

    // custom EHR 登记成功：与 connect 成功同口径——默认勾进计费范围并保存
    // （连接列表由 CustomEhrForm 内部 refresh 广播过来）
    const handleCustomSubmitted = async (name: string) => {
        setCustomOpen(false);
        if (!billingEhrs.includes(name)) {
            form.setFieldValue(['ehr', 'billingEhrs'], [...billingEhrs, name]);
            try {
                await saveEhrSlice();
            } catch (e) {
                // 错误已由全局 toast 展示
            }
        }
    };

    const openScopeEdit = () => {
        setScopeSnap(claimScope);
        setScopeEditing(true);
    };

    const cancelScopeEdit = () => {
        form.setFieldValue(['ehr', 'claimScope'], scopeSnap);
        setScopeEditing(false);
    };

    const saveScopeEdit = async () => {
        setScopeSaving(true);
        try {
            await saveEhrSlice();
            setScopeEditing(false);
            message.success('Changes saved.');
        } catch (e) {
            // 错误已由全局 toast 展示，保持编辑态供用户修正
        } finally {
            setScopeSaving(false);
        }
    };

    const openStartEdit = () => {
        setStartSnap({ mode: claimStartMode, date: claimStartDate });
        setStartEditing(true);
    };

    const cancelStartEdit = () => {
        form.setFieldValue(['ehr', 'claimStartMode'], startSnap.mode);
        form.setFieldValue(['ehr', 'claimStartDate'], startSnap.date);
        setStartEditing(false);
    };

    const saveStartEdit = async () => {
        setStartSaving(true);
        try {
            await saveEhrSlice();
            setStartEditing(false);
            message.success('Changes saved.');
            // today 的起始日由后端生成：保存后重拉一次，specific → today 切换后才有日期可显示
            const vo = await fetchOnboardingVo();
            const c = vo?.claimSettings;
            form.setFieldValue(
                ['ehr', 'claimTodayStartDate'],
                c?.claimStartMode === 'today' ? c.claimStartDate || undefined : undefined,
            );
        } catch (e) {
            // 错误已由全局 toast 展示，保持编辑态供用户修正
        } finally {
            setStartSaving(false);
        }
    };

    // 连接成功 + custom（琥珀 Not supported yet，长期 pending 等内部对接）都入列，
    // 按后端返回顺序展示；Disconnect 与勾选态无关（断开的是连接本身，不是计费范围）
    const checkItems = connections
        .filter((c) => c.connectionStatus === 'connected' || c.isCustom)
        .map((c) => {
            const name = c.ehrSystem;
            return {
                name,
                pending: !!c.isCustom,
                extra: (
                    <>
                        <Button
                            type="link"
                            className={cx(s.connLink, s.connLinkDanger)}
                            onClick={() => handleDisconnect(name)}
                        >
                            Disconnect
                        </Button>
                        {c.isCustom ? (
                            <span className={s.connTagPending}>Not supported yet</span>
                        ) : (
                            <span className={cx(s.connStatus, s.connStatusOk)}>
                                <span className={s.connDot} />
                                Connected
                            </span>
                        )}
                    </>
                ),
            };
        });

    const currentScope = SCOPE_OPTIONS.find((o) => o.value === claimScope) || SCOPE_OPTIONS[0];
    const currentStart = START_OPTIONS.find((o) => o.value === claimStartMode) || START_OPTIONS[0];
    // 读态标题显示具体日期（与 review 页一致）：specific → "From {date}"，today → "Starting from {固定起始日}"
    let startReadTitle = currentStart.title;
    let startReadDesc = currentStart.desc;
    if (claimStartMode === 'specific' && claimStartDate) {
        startReadTitle = `From ${dayjs(claimStartDate).format('MMM D, YYYY')}`;
    } else if (claimStartMode === 'today' && claimTodayStartDate) {
        startReadTitle = `Starting from ${formatTodayStartDate(claimTodayStartDate)}`;
        startReadDesc = 'Only encounters from that date onward.';
    }

    return (
        <div className={s.ehrScope}>
            {/* 注册辅助字段：useWatch 只对已注册字段生效 */}
            <Form.Item name={['ehr', 'billingEhrs']} noStyle>
                <HiddenFormField />
            </Form.Item>
            <Form.Item name={['ehr', 'claimTodayStartDate']} noStyle>
                <HiddenFormField />
            </Form.Item>

            <div className={s.connTitle}>EHR connections</div>
            <div className={s.connList}>
                {checkItems.length ? (
                    <EhrBillingCheckList items={checkItems} value={billingEhrs} onChange={handleCheckChange} />
                ) : (
                    <div className={s.connEmpty}>No EHR connected yet.</div>
                )}
            </div>

            <div className={s.moreTitle}>Deploy agents to more EHR accounts</div>
            <div className={s.moreSub}>Bring managed billing to your other EHR accounts.</div>
            <div className={s.ehrGrid}>
                {availableEhrs.map((ehr) => {
                    // 按真实连接状态渲染：connected 不可点；error 可点进入重连；none 可点进入连接
                    const status = connStatusOf(ehr.name);
                    if (status === 'connected') {
                        return (
                            <div key={ehr.name} className={cx(s.ehrCard, s.ehrCardConnected)}>
                                <EhrBadge ehr={ehrByName(ehr.name)} />
                                <span className={s.ehrCardText}>
                                    <span className={s.ehrCardName}>{ehr.name}</span>
                                    <span className={s.ehrCardOk}>
                                        <CheckOutlined /> Connected
                                    </span>
                                </span>
                            </div>
                        );
                    }
                    return (
                        <button key={ehr.name} type="button" className={s.ehrCard} onClick={() => setFlowEhr(ehr.name)}>
                            <EhrBadge ehr={ehrByName(ehr.name)} />
                            <span className={s.ehrCardText}>
                                <span className={s.ehrCardName}>{ehr.name}</span>
                                {status === 'error' && (
                                    <span className={s.ehrCardErr}>Connection lost — click to reconnect</span>
                                )}
                            </span>
                        </button>
                    );
                })}
                {upcomingEhrs.map((ehr) => (
                    <div key={ehr.name} className={cx(s.ehrCard, s.ehrCardUpcoming)}>
                        <EhrBadge ehr={ehrByName(ehr.name)} />
                        <span className={s.ehrCardText}>
                            <span className={s.ehrCardName}>{ehr.name}</span>
                            <span className={s.ehrCardUpcomingTag}>Upcoming</span>
                        </span>
                    </div>
                ))}
                <button type="button" className={cx(s.ehrCard, s.ehrCardOther)} onClick={() => setCustomOpen(true)}>
                    <span className={s.otherPlusIcon}>
                        <PlusOutlined />
                    </span>
                    <span className={s.ehrCardText}>
                        <span className={s.ehrCardName}>My EHR isn&apos;t listed</span>
                        <span className={s.ehrCardSub}>Tell us what you use</span>
                    </span>
                </button>
            </div>

            <div className={s.scopeTitleRow}>
                <div className={s.connTitle}>Which patients should Klarity bill for?</div>
                {scopeEditing ? (
                    <div className={s.scopeBtns}>
                        <Button onClick={cancelScopeEdit} disabled={scopeSaving}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={saveScopeEdit} loading={scopeSaving}>
                            Save changes
                        </Button>
                    </div>
                ) : (
                    <Button onClick={openScopeEdit}>Edit</Button>
                )}
            </div>
            {scopeEditing ? (
                <>
                    <ClaimScopeField />
                    {claimScope === 'tagged' && (
                        <p className={s.scopeTip}>
                            Tip: add the &quot;Klarity Billing&quot; tag to any patient in your EHR to include them.
                        </p>
                    )}
                </>
            ) : (
                <>
                    <Form.Item name={['ehr', 'claimScope']} noStyle>
                        <HiddenFormField />
                    </Form.Item>
                    <div className={s.scopeRead}>
                        <div className={s.scopeReadVal}>{currentScope.title}</div>
                        <div className={s.scopeReadDesc}>{currentScope.desc}</div>
                    </div>
                </>
            )}

            <div className={s.scopeTitleRow}>
                <div className={s.connTitle}>When should Klarity start processing claims?</div>
                {startEditing ? (
                    <div className={s.scopeBtns}>
                        <Button onClick={cancelStartEdit} disabled={startSaving}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={saveStartEdit} loading={startSaving}>
                            Save changes
                        </Button>
                    </div>
                ) : (
                    <Button onClick={openStartEdit}>Edit</Button>
                )}
            </div>
            {startEditing ? (
                <ClaimStartFields />
            ) : (
                <>
                    <Form.Item name={['ehr', 'claimStartMode']} noStyle>
                        <HiddenFormField />
                    </Form.Item>
                    <Form.Item name={['ehr', 'claimStartDate']} noStyle>
                        <HiddenFormField />
                    </Form.Item>
                    <div className={s.scopeRead}>
                        <div className={s.scopeReadVal}>{startReadTitle}</div>
                        <div className={s.scopeReadDesc}>{startReadDesc}</div>
                    </div>
                </>
            )}

            {flowEhr && (
                <EhrConnectFlow ehrName={flowEhr} open onClose={() => setFlowEhr(null)} onSuccess={handleFlowSuccess} />
            )}
            <CustomEhrModal
                open={customOpen}
                onClose={() => setCustomOpen(false)}
                onSubmitted={handleCustomSubmitted}
            />
        </div>
    );
}
